import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { LogIn, Package, FileText, User } from 'lucide-react';
import { mockUser, mockTrackingOrders } from '../mock';

export const ClientArea = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [trackingCode, setTrackingCode] = useState('');
  const [trackedOrder, setTrackedOrder] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginData.email === mockUser.email && loginData.password === mockUser.password) {
      setIsLoggedIn(true);
      toast({
        title: "Login realizado com sucesso!",
        description: `Bem-vindo, ${mockUser.name}!`,
      });
    } else {
      toast({
        title: "Erro no login",
        description: "Email ou senha incorretos. Tente: cliente@empresa.com / demo123",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setLoginData({ email: '', password: '' });
    toast({
      title: "Logout realizado",
      description: "Até logo!",
    });
  };

  const handleTracking = (e) => {
    e.preventDefault();
    const order = mockTrackingOrders.find(o => o.id === trackingCode.toUpperCase());
    if (order) {
      setTrackedOrder(order);
      toast({
        title: "Pedido encontrado!",
        description: `Status: ${order.status}`,
      });
    } else {
      toast({
        title: "Pedido não encontrado",
        description: "Verifique o código e tente novamente.",
        variant: "destructive"
      });
      setTrackedOrder(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Entregue':
        return 'bg-green-500';
      case 'Em trânsito':
        return 'bg-blue-500';
      case 'Aguardando coleta':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-[#0A2540] mb-4">
              Área do Cliente
            </h1>
            <p className="text-xl text-gray-600">
              Gerencie seus fretes e acompanhe suas entregas
            </p>
          </div>

          {!isLoggedIn ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="shadow-lg">
                <CardHeader className="bg-gradient-to-r from-[#0A2540] to-[#8B3A9C] text-white rounded-t-lg">
                  <CardTitle className="flex items-center text-2xl">
                    <LogIn className="mr-2" />
                    Login
                  </CardTitle>
                  <CardDescription className="text-gray-200">
                    Acesse sua conta para gerenciar seus fretes
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        placeholder="cliente@empresa.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Senha</Label>
                      <Input 
                        id="password"
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        placeholder="demo123"
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#0A2540] hover:bg-[#8B3A9C]">
                      Entrar
                    </Button>
                    <p className="text-sm text-gray-500 text-center">
                      Demo: cliente@empresa.com / demo123
                    </p>
                  </form>
                </CardContent>
              </Card>

              <Card className="shadow-lg">
                <CardHeader className="bg-gradient-to-r from-[#8B3A9C] to-[#F4D03F] text-white rounded-t-lg">
                  <CardTitle className="flex items-center text-2xl">
                    <Package className="mr-2" />
                    Rastreamento
                  </CardTitle>
                  <CardDescription className="text-gray-100">
                    Rastreie sua encomenda sem fazer login
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <form onSubmit={handleTracking} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="tracking">Código de Rastreamento</Label>
                      <Input 
                        id="tracking"
                        value={trackingCode}
                        onChange={(e) => setTrackingCode(e.target.value)}
                        placeholder="VL2025001"
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#8B3A9C] hover:bg-[#0A2540]">
                      Rastrear
                    </Button>
                    <p className="text-sm text-gray-500 text-center">
                      Teste: VL2025001, VL2025002, VL2025003
                    </p>
                  </form>

                  {trackedOrder && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="text-sm text-gray-600">Pedido</p>
                          <p className="font-bold text-[#0A2540]">{trackedOrder.id}</p>
                        </div>
                        <Badge className={getStatusColor(trackedOrder.status)}>
                          {trackedOrder.status}
                        </Badge>
                      </div>
                      <div className="space-y-2 mb-4">
                        <p className="text-sm"><span className="font-semibold">Origem:</span> {trackedOrder.origin}</p>
                        <p className="text-sm"><span className="font-semibold">Destino:</span> {trackedOrder.destination}</p>
                        <p className="text-sm"><span className="font-semibold">Data:</span> {trackedOrder.date}</p>
                      </div>
                      <Progress value={trackedOrder.progress} className="h-2" />
                      <p className="text-xs text-gray-500 mt-2 text-center">{trackedOrder.progress}% concluído</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-[#0A2540] to-[#8B3A9C] text-white rounded-t-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl mb-2">Bem-vindo, {mockUser.name}!</CardTitle>
                    <CardDescription className="text-gray-200">{mockUser.company}</CardDescription>
                  </div>
                  <Button variant="outline" onClick={handleLogout} className="bg-white text-[#0A2540] hover:bg-gray-100">
                    Sair
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <Tabs defaultValue="pedidos" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="pedidos">Meus Pedidos</TabsTrigger>
                    <TabsTrigger value="historico">Histórico</TabsTrigger>
                    <TabsTrigger value="perfil">Perfil</TabsTrigger>
                  </TabsList>
                  <TabsContent value="pedidos" className="space-y-4 mt-6">
                    {mockTrackingOrders.map((order) => (
                      <div key={order.id} className="p-4 border-2 rounded-lg hover:border-[#8B3A9C] transition-colors">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-bold text-[#0A2540]">{order.id}</p>
                            <p className="text-sm text-gray-600">{order.date}</p>
                          </div>
                          <Badge className={getStatusColor(order.status)}>
                            {order.status}
                          </Badge>
                        </div>
                        <div className="space-y-1 mb-3">
                          <p className="text-sm"><span className="font-semibold">Origem:</span> {order.origin}</p>
                          <p className="text-sm"><span className="font-semibold">Destino:</span> {order.destination}</p>
                        </div>
                        <Progress value={order.progress} className="h-2" />
                        <p className="text-xs text-gray-500 mt-2">{order.progress}% concluído</p>
                      </div>
                    ))}
                  </TabsContent>
                  <TabsContent value="historico" className="mt-6">
                    <p className="text-gray-600 text-center py-8">
                      Seu histórico de fretes aparecerá aqui.
                    </p>
                  </TabsContent>
                  <TabsContent value="perfil" className="mt-6 space-y-4">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                        <div className="w-12 h-12 bg-[#8B3A9C] rounded-full flex items-center justify-center">
                          <User className="text-white" size={24} />
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A2540]">{mockUser.name}</p>
                          <p className="text-sm text-gray-600">{mockUser.email}</p>
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="font-semibold text-[#0A2540] mb-2">Empresa</p>
                        <p className="text-gray-600">{mockUser.company}</p>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};
